import { CSSProperties, Fragment, ReactNode } from "react";
import NextImage from "next/image";
import { Accordion } from "./accordion";
import { Alert, AlertDescription } from "./alert";
import { FaqAccordionItem } from "./faq-accordion-item";
import { normalizeSocialEmbedUrl } from "./social-embed";
import { SocialEmbedPlayer } from "./social-embed-player";
import { Typography } from "./typography";
import { CreatorCard } from "./creator/creator-card";
import {
  articleBodyClassName,
  articleBulletListClassName,
  articleBulletListItemClassName,
  articleHeadingClassName,
  articleImageClassName,
  articleImageFigureClassName,
  articleImageMediaClassName,
  articleLinkClassName,
  articleListClassName,
  articleListItemClassName,
  articleListNumber,
  articleOpeningHeadingClassName,
  articleOrderedIndexClassName,
  articleQuoteAvatarClassName,
  articleQuoteBoxClassName,
  articleQuoteDescriptionClassName,
  articleQuoteNameClassName,
  articleQuoteProfileClassName,
  articleQuoteSectionClassName,
  articleQuoteTextClassName,
  articleSeparatorLineClassName,
  articleYoutubeEmbedWrapClassName,
} from "./article-typography";

type PlateText = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
};

type PlateNode = {
  id?: string;
  type?: string;
  url?: string;
  src?: string;
  title?: string;
  language?: string;
  lang?: string;
  checked?: boolean;
  icon?: string;
  backgroundColor?: string;
  caption?: (PlateNode | PlateText)[];
  indent?: number;
  listStyleType?: string;
  listStart?: number;
  children?: (PlateNode | PlateText)[];
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
  // Creator card node fields
  creatorId?: number;
  name?: string;
  avatarSrc?: string | null;
  tiktokHandle?: string | null;
  instagramHandle?: string | null;
  bio?: string;
  niches?: string[];
  slug?: string;
  quote?: string;
  description?: string;
};

const isText = (node: PlateNode | PlateText): node is PlateText =>
  typeof node.text === "string";

const isOrderedListStyle = (listStyleType?: string) =>
  !!listStyleType &&
  [
    "decimal",
    "lower-alpha",
    "upper-alpha",
    "lower-roman",
    "upper-roman",
  ].includes(listStyleType);

const formatArticleQuote = (quote: string) => {
  const trimmed = quote.trim();
  if (!trimmed) {
    return "";
  }
  if (/^[‘“"'`]/.test(trimmed)) {
    return trimmed;
  }
  return `‘${trimmed}’`;
};

const plainText = (node?: PlateNode | PlateText): string => {
  if (!node) return "";
  if (isText(node)) return node.text;
  if (!node.children?.length) return "";
  return node.children.map((child) => plainText(child)).join("");
};

type RenderContext = {
  headings: {
    id: string;
    level: number;
    title: string;
  }[];
  listNumber?: number;
  tightOpeningHeading?: boolean;
};

const headingClassName = (
  level: keyof typeof articleHeadingClassName,
  tightOpeningHeading: boolean | undefined,
) =>
  tightOpeningHeading
    ? articleOpeningHeadingClassName[level]
    : articleHeadingClassName[level];

const renderLeaf = (node: PlateText, key: string | number): ReactNode => {
  const base: ReactNode = node.text;
  const bolded = node.bold ? <strong>{base}</strong> : base;
  const italicised = node.italic ? <em>{bolded}</em> : bolded;
  const underlined = node.underline ? <u>{italicised}</u> : italicised;
  const struckThrough = node.strikethrough ? <s>{underlined}</s> : underlined;
  const content = node.code ? (
    <code className="bg-muted rounded px-1 py-0.5 font-mono text-[0.85em] leading-normal">
      {struckThrough}
    </code>
  ) : (
    struckThrough
  );
  return <Fragment key={key}>{content}</Fragment>;
};

const renderChildren = (
  children: (PlateNode | PlateText)[],
  context: RenderContext,
): ReactNode =>
  children.map((child, i) =>
    isText(child) ? renderLeaf(child, i) : renderNode(child, i, context),
  );

const renderListItem = (
  node: PlateNode,
  kids: ReactNode,
  key: string | number,
  listNumber?: number,
) => {
  if (node.listStyleType === "todo") {
    return (
      <li
        key={key}
        className={node.checked ? "list-none text-muted-foreground line-through" : "list-none"}
      >
        <input
          type="checkbox"
          checked={!!node.checked}
          readOnly
          aria-label="Todo item"
          className="mr-2 align-middle"
        />
        {kids}
      </li>
    );
  }

  const ordered = isOrderedListStyle(node.listStyleType);
  const index = listNumber ?? articleListNumber(node.listStart);

  if (!ordered) {
    return (
      <li
        key={key}
        className={
          node.checked
            ? `${articleBulletListItemClassName} text-muted-foreground line-through`
            : articleBulletListItemClassName
        }
      >
        {kids}
      </li>
    );
  }

  return (
    <li key={key} className={articleListItemClassName}>
      <span aria-hidden className={articleOrderedIndexClassName}>
        {index}
      </span>
      <span className="min-w-0 flex-1">{kids}</span>
    </li>
  );
};

const renderNode = (
  node: PlateNode,
  key: string | number,
  context: RenderContext,
): ReactNode => {
  if (isText(node)) return renderLeaf(node, key);

  const kids = node.children ? renderChildren(node.children, context) : null;
  const mediaUrl =
    typeof node.url === "string"
      ? node.url
      : typeof node.src === "string"
        ? node.src
        : undefined;
  const safeMediaUrl = mediaUrl?.trim() || undefined;
  const normalizedSocialEmbed = safeMediaUrl
    ? normalizeSocialEmbedUrl(safeMediaUrl)
    : null;
  const headingId = node.id ?? `heading-${key}`;

  switch (node.type) {
    case "h1":
      return (
        <Typography
          key={key}
          as="h1"
          id={headingId}
          weight="semibold"
          className={headingClassName("h1", context.tightOpeningHeading)}
        >
          {kids}
        </Typography>
      );
    case "h2":
      return (
        <Typography
          key={key}
          as="h2"
          id={headingId}
          weight="semibold"
          className={headingClassName("h2", context.tightOpeningHeading)}
        >
          {kids}
        </Typography>
      );
    case "h3":
      return (
        <Typography
          key={key}
          as="h3"
          id={headingId}
          weight="semibold"
          className={headingClassName("h3", context.tightOpeningHeading)}
        >
          {kids}
        </Typography>
      );
    case "h4":
      return (
        <Typography
          key={key}
          as="h4"
          id={headingId}
          weight="semibold"
          className="font-poppins mt-5 mb-1 text-xl leading-[1.1] tracking-[-0.02em] text-primary-600 sm:text-2xl"
        >
          {kids}
        </Typography>
      );
    case "h5":
      return (
        <Typography
          key={key}
          as="h5"
          id={headingId}
          weight="semibold"
          className="font-poppins mt-4 mb-1 text-lg leading-[1.1] text-primary-600"
        >
          {kids}
        </Typography>
      );
    case "h6":
      return (
        <Typography
          key={key}
          as="h6"
          id={headingId}
          weight="semibold"
          className="font-poppins mt-4 mb-1 text-base leading-[1.1] text-primary-600"
        >
          {kids}
        </Typography>
      );
    case "blockquote":
      return (
        <Typography
          key={key}
          as="blockquote"
          className="bg-muted/35 my-3 rounded-r-lg border-l-4 border-primary-600 px-3 py-1.5 italic text-primary-600 [&_p]:my-0"
        >
          {kids}
        </Typography>
      );
    case "hr":
      return (
        <hr
          key={key}
          className={`my-[48px] ${articleSeparatorLineClassName}`}
        />
      );
    case "article_quote":
      return (
        <figure key={key} className={articleQuoteSectionClassName}>
          <blockquote className={articleQuoteBoxClassName}>
            <Typography className={articleQuoteTextClassName}>
              {formatArticleQuote(node.quote ?? "")}
            </Typography>
          </blockquote>
          {(node.name || node.description || node.avatarSrc) && (
            <figcaption className={articleQuoteProfileClassName}>
              {node.avatarSrc ? (
                <NextImage
                  alt={node.name ?? ""}
                  className={articleQuoteAvatarClassName}
                  height={48}
                  src={node.avatarSrc}
                  unoptimized
                  width={48}
                />
              ) : (
                <span className={articleQuoteAvatarClassName} />
              )}
              <div>
                {node.name ? (
                  <Typography className={articleQuoteNameClassName}>
                    {node.name}
                  </Typography>
                ) : null}
                {node.description ? (
                  <Typography className={articleQuoteDescriptionClassName}>
                    {node.description}
                  </Typography>
                ) : null}
              </div>
            </figcaption>
          )}
        </figure>
      );
    case "code":
      return (
        <code
          key={key}
          className="bg-muted rounded px-1 py-0.5 font-mono text-[0.85em] leading-normal"
        >
          {kids}
        </code>
      );
    case "code_block":
    case "codeBlock": {
      const code = (node.children ?? [])
        .map((line) => plainText(line as PlateNode))
        .join("\n");
      const language = node.language ?? node.lang;
      return (
        <div key={key} className="my-2 overflow-hidden rounded-md border border-neutral-200 bg-neutral-950">
          {language && (
            <div className="border-b border-neutral-700 bg-neutral-800 px-3 py-1 font-mono text-xs text-neutral-400 uppercase">
              {language}
            </div>
          )}
          <pre className="m-0 overflow-x-auto px-3 py-2">
            <code className="font-mono text-sm leading-normal text-neutral-100 whitespace-pre">{code}</code>
          </pre>
        </div>
      );
    }
    case "toggle":
      // Fallback: top-level toggles are handled by groupNodes/renderGrouped.
      // This branch fires only if a toggle appears as a nested child.
      return (
        <Typography key={key} as="p" weight="medium" className="my-1.5">
          {kids}
        </Typography>
      );
    case "callout": {
      const icon = node.icon || "💡";
      return (
        <Alert
          key={key}
          className="my-3 flex items-start gap-3 [&_p]:my-0"
          style={node.backgroundColor ? { backgroundColor: node.backgroundColor, borderColor: node.backgroundColor } : undefined}
        >
          <span
            className="mt-0.5 shrink-0 text-lg leading-none select-none"
            style={{
              fontFamily:
                '"Apple Color Emoji", "Segoe UI Emoji", NotoColorEmoji, "Noto Color Emoji", "Segoe UI Symbol", "Android Emoji", EmojiSymbols',
            }}
          >
            {icon}
          </span>
          <AlertDescription className="w-full text-inherit">
            {kids}
          </AlertDescription>
        </Alert>
      );
    }
    case "table":
      return (
        <div key={key} className="my-4 overflow-x-auto">
          <table className="min-w-full border-collapse border border-neutral-300">
            <tbody>{kids}</tbody>
          </table>
        </div>
      );
    case "tr":
      return <tr key={key}>{kids}</tr>;
    case "th":
      return (
        <th key={key} className="border border-neutral-300 bg-neutral-50 px-3 py-2 text-left font-semibold [&_p]:my-0">
          {kids}
        </th>
      );
    case "td":
      return (
        <td key={key} className="border border-neutral-300 px-3 py-2 align-top [&_p]:my-0">
          {kids}
        </td>
      );
    case "toc":
      return (
        <nav key={key} className="bg-muted/20 my-4 rounded-md border px-4 py-3" aria-label="Table of contents">
          <Typography weight="semibold" className="font-poppins mb-2 text-base">
            Table of contents
          </Typography>
          {context.headings.length > 0 ? (
            <ul className="m-0 list-none space-y-1 p-0">
              {context.headings.map((heading) => (
                <li
                  key={heading.id}
                  style={{ paddingLeft: `${(heading.level - 1) * 1}rem` }}
                >
                  <a
                    href={`#${heading.id}`}
                    className="text-primary underline underline-offset-4"
                  >
                    {heading.title}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <Typography size="sm" color="neutral" className="m-0">
              Create a heading to display the table of contents.
            </Typography>
          )}
        </nav>
      );
    case "img":
    case "image":
    case "media_image":
      return (
        <figure key={key} className={articleImageFigureClassName}>
          {mediaUrl && (
            <div className={articleImageClassName}>
              <NextImage
                src={mediaUrl}
                alt={node.title ?? plainText(node.caption?.[0])}
                width={0}
                height={0}
                sizes="100vw"
                unoptimized
                className={articleImageMediaClassName}
              />
            </div>
          )}
          {node.caption?.length ? (
            <Typography as="figcaption" size="sm" color="neutral" className="mt-2 text-center">
              {plainText(node.caption[0])}
            </Typography>
          ) : null}
        </figure>
      );
    case "video":
    case "media_video":
      return (
        <figure key={key} className="my-4">
          {mediaUrl && (
            <video
              controls
              className="h-auto w-full rounded-sm"
              src={mediaUrl}
            />
          )}
          {node.caption?.length ? (
            <Typography as="figcaption" size="sm" color="neutral" className="mt-2 text-center">
              {plainText(node.caption[0])}
            </Typography>
          ) : null}
        </figure>
      );
    case "mediaEmbed":
    case "media_embed":
      return (
        <div
          key={key}
          className={
            normalizedSocialEmbed?.provider === "youtube"
            || normalizedSocialEmbed?.provider === "loom"
              ? `my-4 ${articleYoutubeEmbedWrapClassName}`
              : "my-4"
          }
        >
          {normalizedSocialEmbed && (
            <SocialEmbedPlayer
              embed={normalizedSocialEmbed}
              title={node.title ?? "Embedded media"}
            />
          )}
          {safeMediaUrl && !normalizedSocialEmbed && (
            <div className="bg-muted/35 rounded-sm border border-dashed px-3 py-2">
              <Typography size="sm" color="neutral" className="m-0">
                Unsupported embed URL. Open the original link:
              </Typography>
              <a
                href={safeMediaUrl}
                className="text-primary underline underline-offset-4"
                target="_blank"
                rel="noopener noreferrer"
              >
                {safeMediaUrl}
              </a>
            </div>
          )}
        </div>
      );
    case "a":
      return (
        <a
          key={key}
          href={node.url}
          className={articleLinkClassName}
          target="_blank"
          rel="noopener noreferrer"
        >
          {kids}
        </a>
      );
    case "creator_card":
      if (!node.slug || !node.name) return null;
      return (
        <div key={key} className="my-4">
          <CreatorCard
            href={`/creators/${node.slug}`}
            name={node.name}
            avatarSrc={node.avatarSrc ?? null}
            tiktokHandle={node.tiktokHandle ?? null}
            instagramHandle={node.instagramHandle ?? null}
            bio={node.bio ?? ""}
            niches={node.niches ?? []}
          />
        </div>
      );
    case "p":
      if (node.listStyleType) {
        return renderListItem(node, kids, key, context.listNumber);
      }
      return (
        <Typography
          key={key}
          as="p"
          className={`${articleBodyClassName} my-0 leading-normal whitespace-normal`}
        >
          {kids}
        </Typography>
      );
    default:
      if (node.listStyleType) {
        return renderListItem(node, kids, key, context.listNumber);
      }
      return (
        <Typography
          key={key}
          as="p"
          className="my-0 leading-normal whitespace-normal"
        >
          {kids}
        </Typography>
      );
  }
};

type ListGroup = {
  ordered: boolean;
  listStyleType: string;
  start: number;
  items: PlateNode[];
};

type ToggleGroup = {
  toggleNode: PlateNode;
  body: (PlateNode | ListGroup | ToggleGroupList)[];
};

type ToggleGroupList = {
  toggles: ToggleGroup[];
};

const groupNodes = (nodes: PlateNode[]): (PlateNode | ListGroup | ToggleGroupList)[] => {
  const result: (PlateNode | ListGroup | ToggleGroupList)[] = [];
  let currentListGroup: ListGroup | null = null;
  let i = 0;

  while (i < nodes.length) {
    const node = nodes[i];

    if (node.type === "toggle") {
      currentListGroup = null;
      const toggleIndent = node.indent ?? 0;
      const bodyFlat: PlateNode[] = [];
      i++;
      while (i < nodes.length) {
        const next = nodes[i];
        const nextIndent = next.indent ?? 0;
        if (nextIndent > toggleIndent) {
          bodyFlat.push({ ...next, indent: nextIndent - toggleIndent - 1 });
          i++;
        } else {
          break;
        }
      }
      const tg: ToggleGroup = { toggleNode: node, body: groupNodes(bodyFlat) };
      const last = result[result.length - 1];
      if (last && "toggles" in last) {
        last.toggles.push(tg);
      } else {
        result.push({ toggles: [tg] });
      }
      continue;
    }

    if (node.listStyleType) {
      const ordered = isOrderedListStyle(node.listStyleType);
      if (
        currentListGroup &&
        currentListGroup.ordered === ordered &&
        currentListGroup.listStyleType === node.listStyleType
      ) {
        currentListGroup.items.push(node);
      } else {
        currentListGroup = {
          ordered,
          listStyleType: node.listStyleType,
          start: node.listStart ?? 1,
          items: [node],
        };
        result.push(currentListGroup);
      }
      i++;
      continue;
    }

    currentListGroup = null;
    result.push(node);
    i++;
  }

  return result;
};

const renderToggleGroup = (
  tg: ToggleGroup,
  key: number,
  context: RenderContext,
): ReactNode => {
  const { toggleNode, body } = tg;
  return (
    <FaqAccordionItem
      key={key}
      value={String(key)}
      question={renderChildren(toggleNode.children ?? [], context)}
    >
      {body.length > 0 && (
        <div className="[&_p]:my-1">
          {body.map((bodyItem, i) => renderGrouped(bodyItem, i, context))}
        </div>
      )}
    </FaqAccordionItem>
  );
};

const renderGrouped = (
  item: PlateNode | ListGroup | ToggleGroupList,
  key: number,
  context: RenderContext,
): ReactNode => {
  if ("items" in item) {
    const Tag = item.ordered ? "ol" : "ul";
    const isTodo = item.listStyleType === "todo";
    const className = isTodo
      ? "my-3 list-none pl-0"
      : item.ordered
        ? articleListClassName
        : articleBulletListClassName;
    const style: CSSProperties | undefined = item.ordered
      ? undefined
      : {
          listStyleType: isTodo ? "none" : item.listStyleType,
        };

    return (
      <Tag
        key={key}
        role="list"
        start={item.ordered ? articleListNumber(item.start) : undefined}
        className={className}
        style={style}
      >
        {item.items.map((n, i) =>
          renderNode(n, i, {
            ...context,
            listNumber:
              n.listStart != null && n.listStart >= 1
                ? n.listStart
                : articleListNumber(item.start) + i,
          }),
        )}
      </Tag>
    );
  }
  if ("toggles" in item) {
    return (
      <Accordion key={key} type="multiple" className="my-3">
        {item.toggles.map((tg, i) => renderToggleGroup(tg, i, context))}
      </Accordion>
    );
  }
  return renderNode(item, key, context);
};

const isHeadingType = (type?: string): type is string =>
  !!type && ["h1", "h2", "h3", "h4", "h5", "h6"].includes(type);

const isBlankTextBlock = (node: PlateNode) => {
  if (node.listStyleType) {
    return false;
  }
  const type = node.type ?? "p";
  if (type !== "p" && !isHeadingType(type)) {
    return false;
  }
  if (node.children?.some((child) => !isText(child))) {
    return false;
  }
  return plainText(node).trim() === "";
};

const dropLeadingBlankTextBlocks = (nodes: PlateNode[]) => {
  const start = nodes.findIndex((node) => !isBlankTextBlock(node));
  if (start === -1) {
    return [];
  }
  if (start === 0) {
    return nodes;
  }
  return nodes.slice(start);
};

type Props = {
  value: PlateNode[] | string;
  className?: string;
  /** Sit the first heading against the eyebrow instead of the in-article offset. */
  tightOpeningHeading?: boolean;
};

export const PlateRenderer = ({
  value,
  className,
  tightOpeningHeading = false,
}: Props) => {
  const parsedNodes: PlateNode[] =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value) as PlateNode[];
          } catch {
            return [];
          }
        })()
      : value;

  if (!Array.isArray(parsedNodes) || parsedNodes.length === 0) {
    return null;
  }

  const nodes = tightOpeningHeading
    ? dropLeadingBlankTextBlocks(parsedNodes)
    : parsedNodes;

  if (nodes.length === 0) {
    return null;
  }

  // Assign stable fallback ids onto heading nodes that lack an explicit id so
  // that any spread clones produced by groupNodes carry the same id.
  nodes.forEach((node, index) => {
    if (isHeadingType(node?.type) && !node.id) {
      node.id = `heading-${index}`;
    }
  });

  const headings = nodes
    .filter((node): node is PlateNode => isHeadingType(node?.type))
    .map((heading) => ({
      id: heading.id ?? "",
      level: Number(heading.type?.replace("h", "")) || 1,
      title: plainText(heading).trim(),
    }))
    .filter((heading) => heading.title.length > 0);

  const grouped = groupNodes(nodes);
  const context: RenderContext = { headings };

  return (
    <div className={["max-w-none", className].filter(Boolean).join(" ")}>
      {grouped.map((item, i) =>
        renderGrouped(item, i, {
          ...context,
          tightOpeningHeading: tightOpeningHeading && i === 0,
        }),
      )}
    </div>
  );
};
